import type { RequirementGroupSummary } from '@ben/columbia/api/requirements';
import { GROUPS_PAGE_SIZE } from '@ben/columbia/api/requirements';
import { EmployerCombobox } from '@ben/columbia/components/employer/employer-combobox';
import EmptyState from '@ben/orion/empty-state';
import Loader from '@ben/orion/loader';
import { PaginationControls } from '@ben/orion/pagination';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { useRequirementsV10GroupsList } from '@ben/api/gen';
import { useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Cards, Metadata, Muted, Page, Row, Stack } from './shared';
import { StatusTag } from './status';
import type { WorkspaceStatus } from './status';

const RequirementsScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const employer = searchParams.get('employer');
  const employerName = searchParams.get('employerName') ?? undefined;
  // URL is 1-based; PaginationControls is 0-based — convert at the boundary.
  const pageParam = Number(searchParams.get('page') ?? '1');
  const page = Math.max(1, Number.isFinite(pageParam) ? pageParam : 1);

  const { data, isLoading, isError } = useRequirementsV10GroupsList(
    {
      employer: employer ? Number(employer) : 0,
      // Domain groups belong to their parent, so listing them here would
      // show them as peers of the group they sit inside.
      rootsOnly: true,
      page,
      pageSize: GROUPS_PAGE_SIZE,
    },
    {
      query: {
        enabled: Boolean(employer),
      },
    }
  );

  const handleSelectEmployer = useCallback(
    (pk: number, name: string) => {
      // Reset page when the employer changes — page 2 of employer A is not
      // page 2 of employer B.
      const next = new URLSearchParams(searchParams);
      next.set('employer', String(pk));
      next.set('employerName', name);
      next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const onPageChange = useCallback(
    // PaginationControls sends a 0-based index; the API and URL use 1-based.
    (pageIndex: number) => {
      const next = new URLSearchParams(searchParams);
      const nextPage = pageIndex + 1;
      if (nextPage === 1) {
        next.delete('page');
      } else {
        next.set('page', String(nextPage));
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const groups = useMemo(() => data?.results ?? [], [data]);
  // A group with no entity is one nobody has classified yet. Splitting them
  // out says so on the page rather than hiding them behind a filter.
  const classified = groups.filter((group) => group.entity !== null);
  const unclassified = groups.filter((group) => group.entity === null);

  const totalGroups = data?.count ?? 0;
  const totalPages = Math.ceil(totalGroups / GROUPS_PAGE_SIZE);
  const fromItem = (page - 1) * GROUPS_PAGE_SIZE + 1;
  const toItem = Math.min(page * GROUPS_PAGE_SIZE, totalGroups);

  return (
    <Page>
      <div>
        <Typography variant={TypographyVariant.h1}>Requirements</Typography>
        <Lede>
          What an employer has told us their benefits should do, and how far
          each set of answers has got towards being configured in Ben.
        </Lede>
      </div>

      <EmployerCombobox
        selectedId={employer ? Number(employer) : undefined}
        selectedName={employerName}
        onChange={handleSelectEmployer}
      />

      {employer ? null : (
        <EmptyState
          title="Pick an employer"
          info="Choose an employer to see the requirements captured for them."
        />
      )}

      {employer && isLoading ? <Loader /> : null}

      {isError ? <Muted>Could not load requirements.</Muted> : null}

      {employer && !isLoading && totalGroups === 0 ? (
        <EmptyState
          title="Nothing captured yet"
          info="No requirements have been captured for this employer."
        />
      ) : null}

      {classified.length > 0 ? (
        <GroupSection
          title="Benefits"
          count={classified.length}
          groups={classified}
        />
      ) : null}

      {unclassified.length > 0 ? (
        <GroupSection
          title="Not classified yet"
          count={unclassified.length}
          info="Nobody has said what these are for. Open one to see what was captured."
          groups={unclassified}
        />
      ) : null}

      {totalGroups > 0 ? (
        <PaginationControls
          currentPageIndex={page - 1}
          totalPages={totalPages}
          totalItems={totalGroups}
          fromItems={fromItem}
          toItems={toItem}
          pageSize={GROUPS_PAGE_SIZE}
          onPageChange={onPageChange}
        />
      ) : null}
    </Page>
  );
};

export default RequirementsScreen;

// ─── GroupSection ─────────────────────────────────────────────────────────────

const GroupSection = ({
  title,
  count,
  info,
  groups,
}: {
  title: string;
  count: number;
  info?: string;
  groups: RequirementGroupSummary[];
}) => (
  <section>
    <Row $gap={2}>
      <Typography variant={TypographyVariant.h2}>{title}</Typography>
      <Count>{count}</Count>
    </Row>
    {info ? <Lede>{info}</Lede> : null}
    <GroupCards>
      {groups.map((group) => (
        <GroupCard key={group.id} group={group} />
      ))}
    </GroupCards>
  </section>
);

// ─── GroupCard ────────────────────────────────────────────────────────────────

const GroupCard = ({ group }: { group: RequirementGroupSummary }) => {
  // The tree roll-up, not the root's own: requirements live on the domain
  // children, so a root's own figure is almost always zero.
  const outstanding = group.statusCountsInTree.dirty;
  const domains = group.childGroupCount;

  return (
    <Card to={`/requirements/groups/${group.id}`}>
      <Stack $gap={1}>
        <Typography variant={TypographyVariant.bodyLg2}>
          {group.name}
        </Typography>
        <Muted>
          {group.statusCountsInTree.total} requirement
          {group.statusCountsInTree.total === 1 ? '' : 's'}
          {domains > 0
            ? ` across ${domains} domain${domains === 1 ? '' : 's'}`
            : ''}
        </Muted>
      </Stack>

      {group.intent ? <Intent>{group.intent}</Intent> : null}

      <Metadata>
        <dt>Benefit</dt>
        <dd>{group.benefit ? group.benefit.name : 'Not built yet'}</dd>
        <dt>Programme</dt>
        <dd>
          {group.programmes.length > 0
            ? group.programmes.map((p) => p.name).join(', ')
            : 'None'}
        </dd>
        <dt>Latest run</dt>
        <dd>
          {group.latestRun ? (
            <Row $gap={1}>
              {group.latestRun.id}
              <StatusTag status={group.latestRun.status as WorkspaceStatus} />
            </Row>
          ) : (
            'None'
          )}
        </dd>
      </Metadata>

      <CardFooter>
        {group.pendingChanges.length > 0 ? (
          <Highlight>
            {group.pendingChanges.length} change
            {group.pendingChanges.length === 1 ? '' : 's'} awaiting a decision
          </Highlight>
        ) : outstanding > 0 ? (
          <Muted>{outstanding} still to configure</Muted>
        ) : (
          <Muted>Nothing outstanding</Muted>
        )}
      </CardFooter>
    </Card>
  );
};

// ─── Styled components ────────────────────────────────────────────────────────

const GroupCards = styled(Cards)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Lede = styled.p`
  ${({ theme }) => css`
    margin: ${theme.spacing(1)} 0 0;
    max-width: 62ch;
    color: ${theme.palette.neutral[80]};
    font-size: 0.875rem;
    line-height: 1.5;
  `}
`;

const Count = styled.span`
  ${({ theme }) => css`
    color: ${theme.palette.neutral[80]};
    font-size: 0.875rem;
  `}
`;

const Card = styled(Link)`
  ${({ theme }) => css`
    display: grid;
    gap: ${theme.spacing(3)};
    align-content: start;
    background: ${theme.palette.neutral['00']};
    border: 1px solid ${theme.palette.neutral[60]};
    border-radius: ${theme.borderRadius.md};
    padding: ${theme.spacing(4)};
    color: inherit;
    text-decoration: none;

    &:hover {
      border-color: ${theme.palette.primary.main};
    }

    &:focus-visible {
      outline: 2px solid ${theme.palette.primary.main};
      outline-offset: 2px;
    }
  `}
`;

const Intent = styled.p`
  ${({ theme }) => css`
    margin: 0;
    color: ${theme.palette.neutral[80]};
    font-size: 0.8125rem;
    line-height: 1.5;
  `}
`;

const CardFooter = styled.div`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.palette.neutral[40]};
    padding-top: ${theme.spacing(2)};
  `}
`;

const Highlight = styled.div`
  ${({ theme }) => css`
    color: ${theme.palette.primary.main};
    font-size: 0.8125rem;
    font-weight: 600;
  `}
`;
